<template>
  <v-autocomplete
    class="floc-search"
    v-bind="$attrs"
    v-model="model"
    v-model:search="search"
    :items="searchedFlocs"
    :loading="!!loading"
    :multiple="multiple"
    item-value="id"
    item-title="functional_location"
    return-object
  >
    <template v-if="multiple" #prepend-item>
      <v-list-item title="Select All" @click="toggleSelectAll">
        <template #prepend>
          <v-checkbox-btn
            :indeterminate="selectedSome && !selectedAll"
            :model-value="selectedAll"
          />
        </template>
      </v-list-item>
      <v-divider />
    </template>
    <template #append-item v-if="searchedFlocs.length > 100">
      <v-list-item> Keep Searching To Load More...</v-list-item>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts">
import { FunctionalLocation, CostCenter, PlannerGroup } from "@/classes";
import { onMounted, ref, watch, defineModel, defineProps, computed } from "vue";
import { setupApiQueue } from "../base/api-queue";

const model = defineModel<FunctionalLocation | FunctionalLocation[] | null>();
const search = ref("");
const searchedFlocs = ref<FunctionalLocation[]>([]);

const props = defineProps<{
  flocs?: FunctionalLocation[];
  costCenters?: CostCenter[];
  plannerGroups?: PlannerGroup[];
  multiple?: boolean;
}>();

const { queueApiAction, loading } = setupApiQueue();

const selectedSome = computed(() => {
  if (
    props.multiple &&
    Array.isArray(model.value) &&
    searchedFlocs.value.length > 0
  ) {
    const flocs = model.value;
    return searchedFlocs.value.some(
      (floc) => !!flocs.find((f) => f.id === floc.id)
    );
  }
  return false;
});

const selectedAll = computed(() => {
  if (
    props.multiple &&
    Array.isArray(model.value) &&
    searchedFlocs.value.length > 0
  ) {
    const flocs = model.value;
    return searchedFlocs.value.every(
      (floc) => !!flocs.find((f) => f.id === floc.id)
    );
  }
  return false;
});

const toggleSelectAll = () => {
  if (selectedAll.value) {
    model.value = [];
  } else {
    model.value = searchedFlocs.value.slice();
  }
};

const debounce = (func: () => void, delay: number) => {
  let timeout: number | null = null;
  return () => {
    if (timeout !== null) clearTimeout(timeout);
    timeout = setTimeout(() => func(), delay);
  };
};

const searchAPI = () => {
  queueApiAction(async () => {
    searchedFlocs.value = FunctionalLocation.fromAxiosList<FunctionalLocation>(
      await FunctionalLocation.$axios.$post<FunctionalLocation[]>(
        "functional-location/search",
        {
          search: search.value,
          floc_ids: (props.flocs || []).map((f) => f.id),
          cost_center_ids: (props.costCenters || []).map((c) => c.id),
          planner_group_ids: (props.plannerGroups || []).map((p) => p.id),
        }
      )
    );
  });
};

const searchAPIDebounced = debounce(searchAPI, 350);

watch(search, searchAPIDebounced);

watch(
  () => props.flocs,
  () => searchAPIDebounced()
);

watch(
  () => props.costCenters,
  () => {
    searchAPIDebounced();
  }
);

watch(
  () => props.plannerGroups,
  () => {
    searchAPIDebounced();
  }
);

onMounted(() => {
  searchAPI();
});
</script>

<style scoped>
.floc-search :deep(.v-field__field) {
  overflow-y: auto;
  height: 40px;
}
</style>
